import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  User, Mail, Phone, MapPin, GraduationCap, Award,
  CheckCircle, Clock, Star, BookOpen, Loader2, AlertCircle,
  Calendar, Languages, Briefcase, Shield, ArrowLeft, Users,
  IndianRupee
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { tutorSearchService } from '../services/tutorSearchService';
import { toast } from 'sonner';
import axios from 'axios';

const TutorProfile = () => {
  const { tutorCode } = useParams();
  const navigate = useNavigate();
  const { customer, isAuthenticated } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [tutor, setTutor] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTutorProfile();
  }, [tutorCode]);

  const loadTutorProfile = async () => {
    try {
      setLoading(true);
      const response = await tutorSearchService.getTutor(tutorCode);
      setTutor(response.data || response);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load tutor profile');
      console.error('Error loading tutor:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitQuery = () => {
    if (!isAuthenticated) {
      toast.info('Please login to submit a query');
      navigate('/customer/login');
      return;
    }
    navigate('/customer/teacher-query');
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <Button variant="ghost" onClick={handleBack} className="mb-6 gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6">
                <div className="flex gap-6">
                  <Skeleton className="w-32 h-32 rounded-full" />
                  <div className="flex-1 space-y-3">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-64" />
                  </div>
                </div>
              </Card>
              <Card className="p-6">
                <Skeleton className="h-6 w-32 mb-4" />
                <Skeleton className="h-20 w-full" />
              </Card>
            </div>
            <div>
              <Card className="p-6">
                <Skeleton className="h-40 w-full" />
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <Button variant="ghost" onClick={handleBack} className="mb-6 gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Card className="max-w-md mx-auto p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Tutor Not Found</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={handleBack} variant="outline">
              Go Back
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const getTutorName = () => tutor.name || tutor.tutor_name || 'Tutor';
  const getSubjects = () => {
    const subjects = tutor.subjects || tutor.teaching_subjects || [];
    if (subjects.length > 0 && typeof subjects[0] === 'object') {
      return subjects.map(s => s.name || s.subject_name || s);
    }
    return subjects;
  };
  const getExperience = () => tutor.experience_years || tutor.experience || 0;
  const getRating = () => tutor.rating || tutor.average_rating || 0;
  const getReviews = () => tutor.total_reviews || tutor.reviews || 0;
  const getProfileImage = () => {
    if (tutor.profile_image || tutor.photo) {
      return tutor.profile_image || tutor.photo;
    }
    const name = encodeURIComponent(getTutorName());
    return `https://ui-avatars.com/api/?name=${name}&background=0D8ABC&color=fff&size=200`;
  };
  const getClasses = () => {
    const classes = tutor.classes || tutor.teaching_classes || [];
    if (Array.isArray(classes) && classes.length > 0) {
      return classes.map(c => typeof c === 'object' ? (c.name || c.class_name) : c);
    }
    return [];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <Button variant="ghost" onClick={handleBack} className="mb-6 gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Search
        </Button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <Card className="overflow-hidden border-0 shadow-xl">
              <div className="h-24 bg-gradient-to-r from-blue-600 to-teal-500"></div>
              <CardContent className="pt-0 pb-6 px-6">
                <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4 -mt-12">
                  <div className="relative">
                    <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white">
                      <img
                        src={getProfileImage()}
                        alt={getTutorName()}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(getTutorName())}&background=0D8ABC&color=fff&size=200`;
                        }}
                      />
                    </div>
                    {tutor.is_verified && (
                      <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1.5 shadow-lg">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 text-center sm:text-left pb-2">
                    <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                      <h1 className="text-2xl font-bold text-gray-900">{getTutorName()}</h1>
                      {tutor.is_verified && (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                          <Shield className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>
                    <p className="text-gray-600 mt-1">{tutor.headline || 'Professional Home Tutor'}</p>
                    <div className="flex items-center justify-center sm:justify-start gap-4 mt-2 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        <span>{tutor.city || tutor.location || 'Lucknow'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        <span>{getExperience()}+ years exp</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* About Section */}
            {tutor.bio && (
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-5 w-5 text-blue-600" />
                    About Me
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">{tutor.bio}</p>
                </CardContent>
              </Card>
            )}

            {/* Education & Qualifications */}
            {tutor.qualifications && tutor.qualifications.length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-blue-600" />
                    Education & Qualifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {tutor.qualifications.map((qual, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                        <Award className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div>
                          <h3 className="font-semibold text-gray-900">{qual.degree}</h3>
                          <p className="text-sm text-gray-600">{qual.institution}</p>
                          {qual.year && (
                            <p className="text-xs text-gray-500 mt-1">{qual.year}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Subjects */}
            {getSubjects().length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-blue-600" />
                    Subjects I Teach
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {getSubjects().map((subject, index) => (
                      <Badge key={index} className="bg-blue-100 text-blue-800 hover:bg-blue-200 text-sm px-3 py-1">
                        {subject}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Classes */}
            {getClasses().length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-blue-600" />
                    Classes I Teach
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {getClasses().map((cls, index) => (
                      <Badge key={index} variant="outline" className="text-sm px-3 py-1">
                        {cls}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Languages */}
            {tutor.languages && tutor.languages.length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Languages className="h-5 w-5 text-blue-600" />
                    Languages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tutor.languages.map((language, index) => (
                      <Badge key={index} variant="secondary" className="text-sm px-3 py-1">
                        {language}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Action Card */}
            <Card className="border-0 shadow-xl sticky top-4">
              <CardContent className="p-6">
                {/* Rating */}
                <div className="flex items-center justify-between mb-4 pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                    <span className="font-bold text-lg">{getRating().toFixed(1)}</span>
                    <span className="text-gray-500 text-sm">({getReviews()} reviews)</span>
                  </div>
                </div>

                {/* Price Range */}
                {tutor.hourly_rate && (
                  <div className="mb-4 pb-4 border-b">
                    <div className="flex items-center gap-2 text-gray-600">
                      <IndianRupee className="h-4 w-4" />
                      <span className="font-semibold text-gray-900">{tutor.hourly_rate}/hour</span>
                    </div>
                  </div>
                )}

                {/* Availability */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 text-green-600">
                    <Clock className="h-4 w-4" />
                    <span className="font-medium">Available for Tuition</span>
                  </div>
                </div>

                {/* NOTE: Submit Query for individual tutors is temporarily on hold.
                    Customers can submit queries from the dashboard instead.
                <Button
                  onClick={handleSubmitQuery}
                  className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Users className="h-5 w-5 mr-2" />
                  Submit Query
                </Button>

                {!isAuthenticated && (
                  <p className="text-xs text-center text-gray-500 mt-3">
                    <Link to="/customer/login" className="text-blue-600 hover:underline">
                      Login
                    </Link>
                    {' '}to submit a query
                  </p>
                )}
                */}
              </CardContent>
            </Card>

            {/* Verification Badge */}
            {tutor.is_verified && (
              <Card className="border-0 shadow-lg bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-500 rounded-full p-2">
                      <Shield className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-green-900 text-sm">Verified PriEdu Tutor</h3>
                      <p className="text-xs text-green-700">
                        Background checked & verified
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tutor ID Card */}
            {tutor.tutor_id_code && (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Tutor ID</p>
                      <p className="font-mono font-semibold text-gray-900">{tutor.tutor_id_code}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorProfile;
